import {
  useState,
  useEffect,
  useMemo,
  useTransition,
  useCallback,
} from 'react';
import { debounce } from 'lodash';
import { ContentContainer } from '../../../components/Filters/components/ContentContainer';
import { ControlsPanel } from '../../../components/Filters/components/ControlsPanel';
import { CheckboxList } from '../../../components/Filters/components/CheckboxList';
import { SearchInput } from '../../../components/Filters/components/SearchInput';
import { ItemBadgesContainer } from '../../../components/Filters/components/ItemBadgesContainer';
import { FilterLoader } from '../../../components/Filters/components/FilterLoader';
import { NoResults } from '../../../components/Filters/components/NoResults';
import { API } from '../../../API';
import { showRequestError } from '../../../utils/requestError';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from '../contexts';

export interface SourceGroupOption {
  value: number;
  label: string;
}

export interface SourceGroupsResponse {
  objects: SourceGroupData[];
  total_count: number;
}

export interface SourceGroupData {
  id: number;
  name: string;
}

export const SourceGroupChooser = () => {
  const { t } = useTranslation();
  const { state, updateDraftFilters } = useSearchContext();
  const [sourceGroups, setSourceGroups] = useState<SourceGroupOption[]>([]);
  const [selectedSourceGroups, setSelectedSourceGroups] = useState<SourceGroupOption[]>([]);
  const [defaultOptions, setDefaultOptions] = useState<SourceGroupOption[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isPending, startTransition] = useTransition();

  // Use draft filters from context
  const selected = state.draftFilters.sourceGroups.value.map((s) => s);

  // Fetch default groups on mount
  const fetchDefaultGroups = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.set('size', '30');
    urlParams.set('page', '1');
    urlParams.set('sorting', '-id');

    startTransition(() => {
      API.fetch('GET', `/API/v1/groups?${urlParams.toString()}`)
        .then((data: SourceGroupsResponse) => {
          const options = data.objects.map((group: SourceGroupData) => ({
            value: group.id,
            label: group.name,
          }));
          setDefaultOptions(options);
        })
        .catch((error) => {
          showRequestError(error, t('Source groups'));
        });
    });
  }, []);

  useEffect(() => {
    fetchDefaultGroups();
  }, [fetchDefaultGroups]);

  // Fetch selected source groups when they change (for badges only, don't trigger loader)
  useEffect(() => {
    let isCancelled = false;

    const fetchSelected = async () => {
      if (state.draftFilters.sourceGroups.value.length === 0) {
        if (!isCancelled) {
          setSelectedSourceGroups([]);
        }
        return;
      }

      const urlParams = new URLSearchParams();
      state.draftFilters.sourceGroups.value.forEach((id: number) => {
        urlParams.append('ids', id.toString());
      });

      try {
        const data: SourceGroupsResponse = await API.fetch('GET', `/API/v1/groups?${urlParams.toString()}`);
        if (!isCancelled) {
          setSelectedSourceGroups(
            data.objects
              .sort((a, b) => (a.name > b.name ? 1 : -1))
              .map((group: SourceGroupData) => ({
                value: group.id,
                label: group.name,
              })),
          );
        }
      } catch (error) {
        showRequestError(error, t('Source groups'));
      }
    };

    fetchSelected();

    return () => {
      isCancelled = true;
    };
  }, [state.draftFilters.sourceGroups.value]);

  const getSourceGroups = useMemo(
    () =>
      debounce((inputValue, callback) => {
        startTransition(() => {
          const urlParams = new URLSearchParams();
          urlParams.set('q', inputValue);

          API.fetch(
            'GET',
            `/API/v1/groups?${urlParams.toString()}`,
          )
            .then((data: SourceGroupsResponse) => {
              let optionsList = data.objects
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((group) => ({
                  value: group.id,
                  label: group.name,
                }));
              callback(optionsList);
            })
            .catch((error) => {
              showRequestError(error, t('Source groups'));
            });
        });
      }, 1000),
    [startTransition],
  );

  useEffect(() => {
    return () => {
      getSourceGroups.cancel();
    };
  }, [getSourceGroups]);

  const handleClearAll = () => {
    updateDraftFilters({
      sourceGroups: {
        value: [],
        exclude: false,
      },
    });
  };

  const handleToggleSourceGroup = useCallback((groupId: number) => {
    const currentGroups = state.draftFilters.sourceGroups.value;
    const groupIdNum = Number(groupId);

    if (currentGroups.includes(groupIdNum)) {
      updateDraftFilters({
        sourceGroups: {
          value: currentGroups.filter((id) => id !== groupIdNum),
          exclude: state.draftFilters.sourceGroups.exclude,
        },
      });
    } else {
      updateDraftFilters({
        sourceGroups: {
          value: [...currentGroups, groupIdNum],
          exclude: state.draftFilters.sourceGroups.exclude,
        },
      });
    }
  }, [state.draftFilters.sourceGroups.value, state.draftFilters.sourceGroups.exclude, updateDraftFilters]);

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    getSourceGroups(value, setSourceGroups);
  };

  const listToShow = sourceGroups.length > 0 ? sourceGroups : defaultOptions;
  const isNoResults = !isPending && searchValue && sourceGroups.length === 0;

  // Memoize the checkbox items to prevent recreating on every render
  const checkboxItems = useMemo(() => {
    return listToShow.map((group: SourceGroupOption) => ({
      name: group.label,
      checked: selected.includes(group.value),
      onChange: () => handleToggleSourceGroup(group.value),
      disabled: false,
      icon: null,
    }));
  }, [listToShow, selected, handleToggleSourceGroup]);

  // Memoize badges to prevent recreating on every render
  const badges = useMemo(() => {
    return selectedSourceGroups.map((group: SourceGroupOption) => ({
      name: group.label,
      onChange: () => handleToggleSourceGroup(group.value),
    }));
  }, [selectedSourceGroups, handleToggleSourceGroup]);

  return (
    <ContentContainer
      searchComponent={
        <SearchInput
          value={searchValue}
          onChange={handleSearchChange}
          placeholder={t('Search source groups')}
          hint={t('Use search to find options not shown in the list')}
          id='source-group-search'
        />
      }
      actionsComponent={
        <ControlsPanel
          id="source-groups-filter"
          count={state.draftFilters.sourceGroups.value.length}
          isExclude={state.draftFilters.sourceGroups.exclude}
          showExcludeSwitch={true}
          showSelectAll={false}
          onClearAll={handleClearAll}
          onExcludeClick={() => {
            updateDraftFilters({
              sourceGroups: {
                value: state.draftFilters.sourceGroups.value,
                exclude: !state.draftFilters.sourceGroups.exclude,
              },
            });
          }}
        />
      }
      badgesComponent={
        selectedSourceGroups.length > 0 ? (
          <ItemBadgesContainer badges={badges} />
        ) : undefined
      }
    >
      {isPending ? (
        <FilterLoader />
      ) : isNoResults ? (
        <NoResults />
      ) : (
        <CheckboxList items={checkboxItems} />
      )}
    </ContentContainer>
  );
};
